import { create, StoreApi, UseBoundStore } from 'zustand';
import { Dayjs } from 'dayjs';
import { devtools, persist } from 'zustand/middleware';
import { TPart } from '../components/select-part';
import { PART_2_TEXT } from '../components/pain-condition-level';

type WithSelectors<S> = S extends { getState: () => infer T } ? S & { use: { [K in keyof T]: () => T[K] } } : never;

export const createSelectors = <S extends UseBoundStore<StoreApi<object>>>(_store: S) => {
  let store = _store as WithSelectors<typeof _store>;
  store.use = {};
  for (let k of Object.keys(store.getState())) {
    (store.use as any)[k] = () => store(s => s[k as keyof typeof s]);
  }

  return store;
};

// 对象深层嵌套可选泛型工具
type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export interface TFormValue {
  selectTherapyType: AIDiagnose.TherapyType[];
  userInfo: {
    gender: AIDiagnoseSubmitParams['gender'];
    birthday: Dayjs;
  };
  selectPart: TPart[];
  currentDimension: Record<AIDiagnose.DimensionType, number>;
  targetDimension: TFormValue['currentDimension'];
  markPainArea: AIDiagnose.MarkPainArea[];
  painConditionLevel: Record<
    keyof typeof PART_2_TEXT,
    {
      level: AIDiagnose.PainLevel;
      duration: number;
    }
  >;
  physicalCondition: AIDiagnose.PhysicalConditionItems[];
  physicalConditionLevel: Record<AIDiagnose.PhysicalConditionItems | 'stayUpLate', 2 | 3 | 4>;
  postureCondition: AIDiagnose.PostureAdjustItem[];
  postureConditionLevel: Record<AIDiagnose.PostureAdjustItem, AIDiagnose.PainLevel>;
  tongueDiagnoseReportId: {
    value: number;
  };
}

interface TAIDiagnoseForm {
  isTipsPlayOK: boolean;
  currentStep: number;
  prevBtnConfig: {
    show: boolean;
    text?: string;
    disable?: boolean;
    action?: VoidFunc;
    loading?: boolean;
  };
  markPainScreenshotUrl: string;
  nextBtnConfig: Omit<TAIDiagnoseForm['prevBtnConfig'], 'show'>;
  formValue: TFormValue;
}

const initialStoreValue = {
  isTipsPlayOK: false,
  currentStep: -2,
  prevBtnConfig: {
    show: true,
    disable: false,
    action: () => {},
  },
  nextBtnConfig: {
    disable: false,
    action: () => {},
  },
  markPainScreenshotUrl: '',
  formValue: {
    selectTherapyType: [],
    userInfo: {
      gender: null,
      birthday: null,
    },
    selectPart: [],
    currentDimension: {
      waistCircumference: 0,
      backThickness: 0,
      leftThighCircumference: 0,
      rightThighCircumference: 0,
      leftCalfCircumference: 0,
      rightCalfCircumference: 0,
      leftArmCircumference: 0,
      rightArmCircumference: 0,
    },
    targetDimension: {
      waistCircumference: 0,
      backThickness: 0,
      leftThighCircumference: 0,
      rightThighCircumference: 0,
      leftCalfCircumference: 0,
      rightCalfCircumference: 0,
      leftArmCircumference: 0,
      rightArmCircumference: 0,
    },
    markPainArea: [],
    painConditionLevel: null,
    physicalCondition: [],
    physicalConditionLevel: null,
    postureCondition: [],
    postureConditionLevel: null,
    tongueDiagnoseReportId: {
      value: null,
    },
  },
};

export const useAIDiagnoseStore = createSelectors(
  create(
    devtools(
      persist<TAIDiagnoseForm>(() => initialStoreValue, {
        name: 'ai-diagnose-form',
      }),
      {
        enabled: __BUILD_ENV__ !== 'prod',
      }
    )
  )
);

export const setIsTipsPlayOK = (payload: boolean) => useAIDiagnoseStore.setState(() => ({ isTipsPlayOK: payload }));

export const setPrevBtnConfig = (payload: TAIDiagnoseForm['prevBtnConfig']) =>
  useAIDiagnoseStore.setState(state => ({ prevBtnConfig: { ...state.prevBtnConfig, ...(payload || {}) } }));

export const setNextBtnConfig = (payload: TAIDiagnoseForm['nextBtnConfig']) =>
  useAIDiagnoseStore.setState(state => ({ nextBtnConfig: { ...state.nextBtnConfig, ...(payload || {}) } }));

export const setCurrentStep = (payload: number | ((step: number) => { currentStep: number })) => {
  if (typeof payload === 'number') {
    useAIDiagnoseStore.setState(() => ({ currentStep: payload }));
  } else if (typeof payload === 'function') {
    useAIDiagnoseStore.setState(state => payload(state.currentStep));
  }
};

export const setMarkPainScreenshotUrl = (payload: string) =>
  useAIDiagnoseStore.setState(() => ({ markPainScreenshotUrl: payload }));

export const setFormValue = (payload: DeepPartial<TFormValue>, config?: { isMerge?: boolean }) => {
  // 是否使用对象合并
  const { isMerge = false } = config || {};

  useAIDiagnoseStore.setState(state => ({
    formValue: {
      ...state.formValue,
      ...Object.keys(payload).reduce(
        (accur, key) => ({
          ...accur,
          [key]:
            isMerge && !Array.isArray(payload[key])
              ? {
                  ...(state.formValue?.[key] || {}),
                  ...payload[key],
                }
              : payload[key],
        }),
        {}
      ),
    },
  }));
};

export const resetAIDiagnoseStore = () => {
  useAIDiagnoseStore.setState(initialStoreValue);
};
